import { z } from 'zod';
import { AuditSchema, DateOnlySchema, IdSchema, InstantSchema } from './common.ts';
import { MoneySchema, addMoney, formatMoney, money, subtractMoney, type Money } from '../money.ts';
import { CostLineSchema, lineOwnerPrice, type CostLine } from './cost-line.ts';
import { PaymentMethodSchema } from './bill.ts';
import { markupForLine } from './catalog.ts';
import type { CoLine } from './change-order.ts';
import { taxOn } from './tax-rate.ts';

/**
 * An owner invoice: what the builder is asking the owner to pay.
 *
 * `docs/features/09-owner-invoices-payments.md` §§1–2. In Phase 5 it is
 * also what closes the last unmeasured column of the job-costing budget:
 * `invoiced`.
 *
 * **The owner never reads this document.** Its lines carry `unitCost` and
 * `markupMillionths`, which are builder cost and margin, and Firestore
 * rules are all-or-nothing per document. The owner reads an
 * `ownerInvoices` mirror (P5-12c), the same answer as `ownerChangeOrders`
 * and `ownerBudgetLines` to the same problem.
 */

/**
 * Where an invoice has got to, as far as a **person** has decided.
 *
 * Three stored states, not the six `docs/DATA_MODEL.md` listed.
 * `partiallyPaid` and `paid` are what the payments add up to — the same
 * decision as a bill's, and for the same reason: a stored one eventually
 * disagrees with the payments beneath it, and this is the document a
 * customer quotes back at you.
 *
 * `pending` is also gone. It was in the list with nothing to mean —
 * pending what, decided by whom? An invoice is either issued or it is not.
 * If an approval step before release is wanted, it is a flag and a state
 * with a permission behind it, which is a task rather than a label.
 */
export const INVOICE_STATUSES = ['unreleased', 'released', 'void'] as const;
export const InvoiceStatusSchema = z.enum(INVOICE_STATUSES);
export type InvoiceStatus = z.infer<typeof InvoiceStatusSchema>;

/**
 * The transitions this product allows.
 *
 * `released → unreleased` exists and is then **blocked by payment** rather
 * than by the table: §5 says "edits require unrelease (blocked once any
 * payment exists; use credit memo instead)", which is a rule about the
 * payments and not about the status. `canUnreleaseInvoice` is where it
 * lives.
 */
export const INVOICE_TRANSITIONS: Readonly<Record<InvoiceStatus, readonly InvoiceStatus[]>> = {
  unreleased: ['released', 'void'],
  released: ['unreleased', 'void'],
  void: [],
};

export function canTransitionInvoice(from: InvoiceStatus, to: InvoiceStatus): boolean {
  return INVOICE_TRANSITIONS[from].includes(to);
}

export const InvoicePaymentSchema = z.object({
  id: IdSchema,
  amount: MoneySchema,
  method: PaymentMethodSchema,
  at: InstantSchema,
  reference: z.string().trim().max(120).optional(),
  recordedBy: IdSchema,
});
export type InvoicePayment = z.infer<typeof InvoicePaymentSchema>;

export const InvoiceSchema = AuditSchema.extend({
  id: IdSchema,
  companyId: IdSchema,
  jobId: IdSchema,
  /** `SFM-0001`, like a purchase order: the job's prefix and a sequence. */
  number: z.string().trim().min(1).max(32),
  title: z.string().trim().min(1).max(200),
  description: z.string().max(20_000).default(''),
  status: InvoiceStatusSchema.default('unreleased'),
  deadline: DateOnlySchema.optional(),
  lines: z.array(CostLineSchema).default([]),
  /**
   * The tax rate as it was when this invoice was issued.
   *
   * Both the id **and** the rate, which looks redundant and is not: the id
   * says which rate was chosen so an accounting sync can map it, and the
   * frozen `rateMillionths` says what was actually charged. Editing the
   * company's rate next year must not rewrite a tax figure somebody has
   * already paid.
   */
  taxRateId: IdSchema.optional(),
  taxRateMillionths: z.number().int().min(0).max(1_000_000).default(0),
  payments: z.array(InvoicePaymentSchema).default([]),
  releasedAt: InstantSchema.optional(),
  /** Stamped when the owner opens it in the portal. §4. */
  viewedByOwnerAt: InstantSchema.optional(),
  /** What this invoice was built from, for provenance rather than as a link. */
  sourceRefs: z.array(z.object({ collection: z.string().min(1), id: IdSchema })).default([]),
  tagIds: z.array(IdSchema).default([]),
});
export type Invoice = z.infer<typeof InvoiceSchema>;

export interface InvoiceTotals {
  /** The owner price of the lines, before tax. */
  readonly subtotal: Money;
  readonly tax: Money;
  readonly total: Money;
  readonly paid: Money;
  /** Total less paid. Negative means the owner has overpaid. */
  readonly balance: Money;
}

/**
 * What an invoice comes to.
 *
 * **Tax is charged on the taxable lines only**, which is why it is not
 * `taxOn(subtotal)`. A line carries `taxable`, and a job that invoices a
 * government fee alongside taxable work would otherwise charge tax on the
 * fee — the same class of error as charging a markup on a permit, which
 * the catalog's `passThrough` exists to prevent.
 *
 * Everything here is derived. `docs/DATA_MODEL.md` listed `subtotal`,
 * `taxTotal`, `total`, `paidAmount` and `balanceDue` as fields; a stored
 * total is a total that eventually disagrees with its lines, and on this
 * document the disagreement is with a customer.
 */
export function invoiceTotals(
  invoice: Pick<Invoice, 'lines' | 'payments' | 'taxRateMillionths'>,
  currency = 'USD',
): InvoiceTotals {
  const zero = money(0, currency);
  const subtotal = invoice.lines.reduce(
    (running, line) => addMoney(running, lineOwnerPrice(line)),
    zero,
  );
  const taxable = invoice.lines
    .filter((line) => line.taxable)
    .reduce((running, line) => addMoney(running, lineOwnerPrice(line)), zero);
  const tax = taxOn(taxable, invoice.taxRateMillionths);
  const total = addMoney(subtotal, tax);
  const paid = invoice.payments.reduce(
    (running, payment) => addMoney(running, payment.amount),
    zero,
  );
  return { subtotal, tax, total, paid, balance: subtractMoney(total, paid) };
}

export type InvoicePaymentState = 'unpaid' | 'partial' | 'paid' | 'overpaid';

/** Derived from the payments, like a bill's. `overpaid` is reported, not clamped. */
export function invoicePaymentState(
  invoice: Pick<Invoice, 'lines' | 'payments' | 'taxRateMillionths'>,
): InvoicePaymentState {
  const { total, paid } = invoiceTotals(invoice);
  if (paid.amount === 0) return 'unpaid';
  if (paid.amount > total.amount) return 'overpaid';
  if (paid.amount >= total.amount) return 'paid';
  return 'partial';
}

/** Only an unreleased invoice's own fields are editable. */
export function isInvoiceEditable(status: InvoiceStatus): boolean {
  return status === 'unreleased';
}

/**
 * Whether a released invoice may be pulled back.
 *
 * Not once anything has been paid against it, per §5: the owner has a
 * document and has acted on it, so changing it is rewriting history
 * somebody paid for. A credit memo is the instrument for that (P5-13),
 * and the refusal says so.
 */
export function canUnreleaseInvoice(invoice: {
  readonly status: InvoiceStatus;
  /** Only the count matters, so the server can pass what it read. */
  readonly payments: readonly unknown[];
}): boolean {
  return invoice.status === 'released' && invoice.payments.length === 0;
}

/**
 * Whether moving an invoice to `to` must give back the credits and
 * deposits applied to it.
 *
 * The maintainer's rule, stated on 2026-10-30: *"we don't want money
 * stranded anywhere. It should be either sitting on the credits and
 * deposits page to be reapplyable on a new invoice or be on an
 * invoice."* Two states, and nothing in between.
 *
 * Voiding was fixed first (SCRUM-281) because that is where it was
 * reported, but **pulling an invoice back strands money the same way**:
 * `onInvoiceWritten` deletes the owner's copy of an unreleased invoice,
 * so the customer can no longer see the document their deposit is
 * earmarked against, while `depositRemaining` goes on counting it as
 * spent. Money attached to a draft is money attached to nothing.
 *
 * **Written as "not released" rather than as a list of the two**, and
 * that is the whole point of the function existing. An enumeration of
 * `void | unreleased` would silently strand money the day a fourth
 * status is added — the safe behaviour has to be the default, and the
 * unsafe one the exception somebody has to type. `INVOICE_STATUSES` is
 * asserted against this in the tests for the same reason.
 *
 * The companion rule is in `credits.ts`: a credit or deposit may only be
 * applied to a **released** invoice. Together they are the invariant —
 * applied money is always attached to a document the customer has
 * actually been asked to pay.
 */
export function transitionFreesAppliedMoney(to: InvoiceStatus): boolean {
  return to !== 'released';
}

/** Whether it may be voided: not once paid, for the same reason as a bill. */
export function canVoidInvoice(invoice: {
  readonly status: InvoiceStatus;
  readonly payments: readonly unknown[];
}): boolean {
  return invoice.status !== 'void' && invoice.payments.length === 0;
}

/**
 * What released invoices have billed the owner, by cost code.
 *
 * The last unmeasured column of `jobCosting`. Three decisions in it:
 *
 * - **Released only.** An unreleased invoice is a draft the owner has not
 *   seen; counting it would report money as invoiced that nobody has been
 *   asked for.
 * - **Owner price, not total.** Tax is not a job cost and does not belong
 *   against a cost code — it is money collected on behalf of a government,
 *   and a budget row showing it would overstate what the job earned.
 * - **A void invoice counts nothing**, like a void bill.
 */
export function invoicedByCostCode(
  invoices: readonly Invoice[],
  currency = 'USD',
): Map<string | null, Money> {
  const byCode = new Map<string | null, Money>();
  for (const invoice of invoices) {
    if (invoice.status !== 'released') continue;
    for (const line of invoice.lines) {
      const key = line.costCodeId ?? null;
      byCode.set(key, addMoney(byCode.get(key) ?? money(0, currency), lineOwnerPrice(line)));
    }
  }
  return byCode;
}

/** The next invoice number for a job: the prefix and a four-digit sequence. */
export function formatInvoiceNumber(prefix: string, sequence: number): string {
  return `${prefix.trim().toUpperCase()}-${String(sequence).padStart(4, '0')}`;
}

export const INVOICE_NUMBER_MAX = 9999;

export function parseInvoiceNumber(raw: string): number | undefined {
  const trimmed = raw.trim();
  const tail = trimmed.slice(trimmed.lastIndexOf('-') + 1);
  if (!/^\d{1,4}$/.test(tail)) return undefined;
  const sequence = Number.parseInt(tail, 10);
  return sequence >= 1 && sequence <= INVOICE_NUMBER_MAX ? sequence : undefined;
}

/**
 * The invoice lines an approved change order implies.
 *
 * A **conversion**, not a copy, and the reason is a shape difference worth
 * knowing: a change order carries `CoLine`, which has a `description` and a
 * `showToOwner`, while every Phase 5 record carries `CostLine`, which has a
 * `title` and a `taxable`. They were never the same type, and a function
 * that pretended otherwise would not compile — which is how this was
 * caught.
 *
 * Two mappings follow from that, and both are decisions:
 *
 * - `description` becomes `title`, because that is the field an invoice
 *   prints.
 * - **`taxable` defaults to true**, because a change order has no opinion
 *   about tax and most work is taxable. The alternative — defaulting to
 *   untaxed — would silently undercharge, which is the worse direction to
 *   be wrong in; somebody can untick a line on the invoice.
 *
 * `showToOwner` is dropped: a line on an invoice is a line the owner is
 * being charged for, so a hidden one would be money with no explanation.
 * A change order line hidden from the owner still reaches them as part of
 * the total, which is what `coTotals` does; on an invoice it has to be
 * visible or absent.
 *
 * The figures are copies with new ids and a `sourceRef`. What was invoiced
 * is a fact about the past, so editing the change order afterwards rewrites
 * nothing — and the owner price is already on the line, so nothing is
 * recomputed: the figure they signed for is the figure they are billed.
 */
export function invoiceLinesFromChangeOrder(
  changeOrderId: string,
  lines: readonly CoLine[],
  makeId: () => string,
): CostLine[] {
  return lines.map((line) => ({
    id: makeId(),
    title: line.description,
    quantityThousandths: line.quantityThousandths,
    unitCost: line.unitCost,
    markupMillionths: line.markupMillionths,
    taxable: true,
    ...(line.unit ? { unit: line.unit } : {}),
    ...(line.costCodeId ? { costCodeId: line.costCodeId } : {}),
    sourceRef: { collection: 'changeOrders', id: changeOrderId, lineId: line.id },
  }));
}

/**
 * Invoice lines from an **estimate** (P5-12d).
 *
 * The line's own markup comes across, because the estimate is where the
 * price was agreed — the same reasoning as a change order's, and the same
 * `sourceRef` so editing the estimate afterwards rewrites nothing that has
 * been billed.
 *
 * `taxable` is **carried**, not forced. An estimate line already says
 * whether it is taxable, and overriding that would quietly re-tax a
 * government fee somebody deliberately marked exempt. (A change order's
 * lines carry no such field, which is why that conversion has to default
 * it.)
 */
export function invoiceLinesFromEstimate(
  estimateId: string,
  lines: readonly CostLine[],
  makeId: () => string,
): CostLine[] {
  return lines.map((line) => ({
    ...line,
    id: makeId(),
    sourceRef: { collection: 'estimates', id: estimateId, lineId: line.id },
  }));
}

/**
 * Invoice lines from a **bill** marked billable to the owner (P5-12d).
 *
 * ## The markup decision
 *
 * A bill is **cost**: what a supplier charged. Recharging it to an owner
 * needs a price, and nothing on the record says what that price should be
 * — a bill's lines carry `markupMillionths: 0`, because the person
 * entering a supplier's invoice is recording what they were charged, not
 * deciding what to charge.
 *
 * So the markup is a **parameter, and it defaults to none**: the lines
 * come across at cost unless somebody says otherwise. Recharging at cost
 * is what "billable to the owner" means most of the time — a reimbursable
 * expense — and the alternative, silently applying a markup from
 * somewhere, would invent a price nobody typed. The caller asks.
 *
 * `taxable` defaults to true for the reason the change order conversion
 * does: an owner invoice is a sale, and the exceptions are rarer than the
 * rule. It is editable on the line afterwards.
 */
export function invoiceLinesFromBill(
  billId: string,
  lines: readonly CostLine[],
  makeId: () => string,
  markupMillionths = 0,
): CostLine[] {
  return lines.map((line) => ({
    ...line,
    id: makeId(),
    markupMillionths,
    sourceRef: { collection: 'bills', id: billId, lineId: line.id },
  }));
}

/**
 * Invoice lines from a **selection** (P5-12e).
 *
 * ## The allowance, which is the whole reason this is not a copy
 *
 * The other three sources carry cost lines. A selection carries the
 * owner's **chosen option and its price**, and the price is already the
 * owner's price — there is no cost and no markup anywhere on the record.
 *
 * That is the easy half. The hard half is the **allowance**, and getting it
 * wrong charges a customer twice. A selection with a $3,000 tile allowance
 * has that $3,000 **in the contract already**; the owner choosing a $4,200
 * package owes the $1,200 difference and nothing more. Billing the full
 * $4,200 would charge them for tiles they have paid for once in the
 * original price — the single worst bug this conversion can introduce, and
 * the kind a customer finds rather than a test.
 *
 * So the amount billed is the price **less the allowance**, which is what
 * `remainingFromAllowance` already computes with the sign the other way.
 * With no allowance the whole price is billed, because then nothing has
 * been paid for yet.
 *
 * An allowance that **covers** the choice bills nothing, and returns no
 * lines rather than a zero one: there is genuinely nothing owed, and a
 * $0.00 row on a customer's invoice invites a question with no answer.
 * `selectionBillable` says which case this is before the picker offers it,
 * so the screen can explain rather than silently adding nothing.
 *
 * `unitCost` carries the amount with **no markup**, and the reason is not
 * laziness: the markup is already inside the price the owner agreed, so
 * adding one would charge it twice, and splitting the agreed price into an
 * invented cost and an invented margin would state two figures nobody
 * knows. Nothing reads an invoice line's `unitCost` as a cost —
 * `jobCosting` takes `invoiced` from the owner price, and the invoice
 * screen shows neither cost nor markup by design (P5-12b).
 */
export type SelectionBillable =
  | { readonly kind: 'billable'; readonly amount: Money }
  | { readonly kind: 'notChosen' }
  | { readonly kind: 'noPrice' }
  | { readonly kind: 'coveredByAllowance'; readonly allowance: Money };

export const SELECTION_BILLABLE_REASON: Readonly<Record<string, string>> = {
  notChosen: 'The owner has not chosen an option yet',
  noPrice: 'The chosen option has no price',
  coveredByAllowance: 'The allowance covers this choice, so there is nothing to bill',
};

export function selectionBillable(
  selection: {
    readonly choices: readonly { readonly id: string; readonly price?: Money }[];
    readonly selectedChoiceId?: string;
  },
  allowance: { readonly amount: Money } | undefined,
): SelectionBillable {
  if (!selection.selectedChoiceId) return { kind: 'notChosen' };
  const choice = selection.choices.find((candidate) => candidate.id === selection.selectedChoiceId);
  if (!choice?.price) return { kind: 'noPrice' };

  // No allowance: nothing has been paid for, so the whole price is owed.
  if (!allowance) return { kind: 'billable', amount: choice.price };

  const over = subtractMoney(choice.price, allowance.amount);
  if (over.amount <= 0) return { kind: 'coveredByAllowance', allowance: allowance.amount };
  return { kind: 'billable', amount: over };
}

export function invoiceLinesFromSelection(
  selectionId: string,
  selection: {
    readonly title: string;
    readonly choices: readonly {
      readonly id: string;
      readonly title: string;
      readonly price?: Money;
    }[];
    readonly selectedChoiceId?: string;
  },
  allowance: { readonly amount: Money; readonly costCodeId?: string } | undefined,
  makeId: () => string,
): CostLine[] {
  const billable = selectionBillable(selection, allowance);
  if (billable.kind !== 'billable') return [];

  const choice = selection.choices.find(
    (candidate) => candidate.id === selection.selectedChoiceId,
  )!;
  const over = allowance !== undefined;

  /**
   * The arithmetic goes in the **title**, not in a description.
   *
   * An amount that is a difference, shown without saying so, reads as a
   * wrong price rather than as a balance — so it has to be said on the
   * document the customer actually gets. It was a `description` first, and
   * that was measured and wrong: `ownerInvoiceFrom` carries only `title`,
   * `quantity`, `unit`, `price` and `taxable` onto the owner's mirror, and
   * the PDF is built from the same fields. A description would have been a
   * careful sentence nobody could read.
   *
   * Truncated, because `CostLine.title` is capped at 200 and a selection
   * title plus a choice title plus the sum can exceed it.
   */
  const note = over
    ? ` — ${formatMoney(choice.price!)} less ${formatMoney(allowance.amount)} allowance`
    : '';
  const title = `${selection.title}: ${choice.title}${note}`;

  return [
    {
      id: makeId(),
      title: title.length > 200 ? `${title.slice(0, 197)}...` : title,
      quantityThousandths: 1000,
      unitCost: billable.amount,
      // The markup is already inside the agreed price. See above.
      markupMillionths: 0,
      taxable: true,
      // The allowance's code, when there is one: it is the only cost code
      // anywhere near a selection, and it is the one the budget set the
      // money aside under.
      ...(allowance?.costCodeId ? { costCodeId: allowance.costCodeId } : {}),
      sourceRef: { collection: 'selections', id: selectionId, lineId: choice.id },
    },
  ];
}

/**
 * Invoice lines from a **catalog item** (P5-12e).
 *
 * The one source with **no agreed price behind it**. A change order and an
 * estimate were signed; a bill is a cost somebody paid; a selection is an
 * option the owner picked. A catalog item is just a price list, so this is
 * the office adding something to an invoice at the rate the catalog says.
 *
 * The markup therefore comes from `markupForLine`, which is the only
 * correct way to read it: an item saying `0` is **passed through at cost**
 * and an item saying nothing **inherits its cost code's default**, and
 * collapsing those two is how a government fee acquires 20% on a
 * customer's invoice. The caller passes the cost code so this file needs no
 * repository.
 *
 * `taxable` defaults to true, like the change order and bill conversions,
 * and is editable on the line afterwards.
 */
export function invoiceLinesFromCatalogItem(
  item: {
    readonly id: string;
    readonly title: string;
    readonly description?: string;
    readonly costCodeId: string;
    readonly costType?: CostLine['costType'];
    readonly unitCost: Money;
    readonly unit?: string;
    readonly markupMillionths?: number;
  },
  code: { readonly defaultMarkupMillionths: number } | null,
  quantityThousandths: number,
  makeId: () => string,
): CostLine[] {
  return [
    {
      id: makeId(),
      title: item.title,
      ...(item.description ? { description: item.description } : {}),
      costCodeId: item.costCodeId,
      ...(item.costType ? { costType: item.costType } : {}),
      quantityThousandths,
      unitCost: item.unitCost,
      markupMillionths: markupForLine(item, code),
      taxable: true,
      ...(item.unit ? { unit: item.unit } : {}),
      // `catalogItemId` rather than a `sourceRef`: the catalog is a price
      // list, not a record this line was taken off, so there is nothing to
      // open and nothing whose later edit anybody would want to trace. The
      // field exists for exactly this.
      catalogItemId: item.id,
    },
  ];
}

/**
 * The invoices a record has been billed on (P5-10d).
 *
 * The readback for `Add from`: a bill marked for recharge, once billed,
 * should say where it went. Read off the invoice lines' `sourceRef`, which
 * is **provenance and not a live link** — the figures on the line are
 * copies, so this answers "was this billed, and on what" without claiming
 * the two will stay equal.
 *
 * Unreleased invoices are **included**. A draft is still where that cost
 * went, and somebody deciding whether to bill a cost again needs to know
 * it is already sitting on a draft — the state comes back with each one so
 * the screen can say which.
 *
 * Generic over the collection, because a change order wants the same
 * sentence and already carries the same `sourceRef`.
 */
export interface BilledOn {
  readonly invoiceId: string;
  readonly number: string;
  readonly title: string;
  readonly status: InvoiceStatus;
  /** What this record contributed, at the price the invoice charges. */
  readonly amount: Money;
}

export function billedOn(
  invoices: readonly Invoice[],
  collection: string,
  recordId: string,
  currency = 'USD',
): BilledOn[] {
  const found: BilledOn[] = [];
  for (const invoice of invoices) {
    const lines = invoice.lines.filter(
      (line) => line.sourceRef?.collection === collection && line.sourceRef.id === recordId,
    );
    if (lines.length === 0) continue;
    found.push({
      invoiceId: invoice.id,
      number: invoice.number,
      title: invoice.title,
      status: invoice.status,
      amount: lines.reduce(
        (running, line) => addMoney(running, lineOwnerPrice(line)),
        money(0, currency),
      ),
    });
  }
  return found.sort((a, b) => a.number.localeCompare(b.number));
}

/**
 * Every payment on the job, newest first (P5-13b).
 *
 * Doc 09 §3's Payments tab. The figures all exist on the invoices already,
 * so this is a **reading order** rather than new behaviour: the person it
 * is for is holding a bank statement and wants the job's payments in one
 * list, not one invoice at a time.
 *
 * Each row carries the invoice it came off, because a payment with no
 * invoice beside it cannot be reconciled against anything. Void invoices
 * are **included**: the money was received, and leaving it out would make
 * this list disagree with the bank — which is the one thing it exists to
 * agree with.
 */
export interface JobPaymentRow {
  readonly id: string;
  readonly invoiceId: string;
  readonly invoiceNumber: string;
  readonly invoiceTitle: string;
  /** Marked, because a payment against a voided invoice needs explaining. */
  readonly invoiceVoid: boolean;
  readonly amount: Money;
  readonly method: InvoicePayment['method'];
  readonly at: string;
  readonly reference?: string;
  readonly recordedBy: string;
}

export function jobPayments(invoices: readonly Invoice[]): JobPaymentRow[] {
  return (
    invoices
      .flatMap((invoice) =>
        invoice.payments.map((payment) => ({
          id: payment.id,
          invoiceId: invoice.id,
          invoiceNumber: invoice.number,
          invoiceTitle: invoice.title,
          invoiceVoid: invoice.status === 'void',
          amount: payment.amount,
          method: payment.method,
          at: payment.at,
          ...(payment.reference ? { reference: payment.reference } : {}),
          recordedBy: payment.recordedBy,
        })),
      )
      // Newest first, and the id as a tie-break so two payments recorded in
      // the same second do not swap places between renders.
      .sort((a, b) => b.at.localeCompare(a.at) || a.id.localeCompare(b.id))
  );
}

/** What those payments add up to, for the strip above the list. */
export function jobPaymentsTotal(rows: readonly JobPaymentRow[], currency = 'USD'): Money {
  return rows.reduce((running, row) => addMoney(running, row.amount), money(0, currency));
}

/** The next invoice the owner is waiting on, for the portal's card (§4). */
export function nextInvoiceDue(invoices: readonly Invoice[]): Invoice | undefined {
  return invoices
    .filter((invoice) => invoice.status === 'released' && invoicePaymentState(invoice) !== 'paid')
    .sort((a, b) => (a.deadline ?? '9999').localeCompare(b.deadline ?? '9999'))[0];
}

/**
 * What an owner is allowed to see of an invoice (P5-12c).
 *
 * A **separate document**, not a filtered read, for the third time in this
 * product and the same reason each time: Firestore rules are all-or-nothing
 * per document, and an invoice's lines carry `unitCost` and
 * `markupMillionths`. The mirror carries a price and no cost at all, which
 * is a stronger guarantee than a rule could express.
 *
 * Only **released** invoices are mirrored. An unreleased one is a draft
 * nobody has been asked about, and a draft appearing in somebody's portal
 * is the kind of mistake that gets noticed by the customer.
 */
export const OwnerInvoiceLineSchema = z.object({
  id: IdSchema,
  title: z.string().min(1).max(200),
  quantityThousandths: z.number().int().positive(),
  unit: z.string().max(20).optional(),
  /** What they are charged for this line. There is no cost field. */
  price: MoneySchema,
  taxable: z.boolean(),
});
export type OwnerInvoiceLine = z.infer<typeof OwnerInvoiceLineSchema>;

export const OwnerInvoiceSchema = z.object({
  id: IdSchema,
  companyId: IdSchema,
  jobId: IdSchema,
  number: z.string().min(1).max(32),
  title: z.string().min(1).max(200),
  description: z.string().max(20_000).default(''),
  deadline: DateOnlySchema.optional(),
  lines: z.array(OwnerInvoiceLineSchema).default([]),
  subtotal: MoneySchema,
  tax: MoneySchema,
  total: MoneySchema,
  paid: MoneySchema,
  /**
   * Credits and deposits applied to this invoice (P5-13).
   *
   * On the owner's copy because the **balance** has to take them off, and
   * a balance the owner cannot account for is worse than no breakdown: a
   * figure reduced by an invisible credit reads as an arithmetic mistake.
   * Default zero so an invoice mirrored before P5-13 existed still parses.
   */
  credits: MoneySchema.default({ amount: 0, currency: 'USD' }),
  deposits: MoneySchema.default({ amount: 0, currency: 'USD' }),
  balance: MoneySchema,
  releasedAt: InstantSchema.optional(),
  viewedByOwnerAt: InstantSchema.optional(),
  updatedAt: InstantSchema,
});
export type OwnerInvoice = z.infer<typeof OwnerInvoiceSchema>;

/**
 * The owner's copy of an invoice.
 *
 * Totals are **copied, not recomputed** by whatever reads the mirror: the
 * figures come from `invoiceTotals` here, so the owner's page and the
 * office's cannot arrive at two different answers. Tax is included as a
 * figure — they are being charged it — but the rate id is not, because it
 * is an accounting mapping and means nothing to them.
 */
export function ownerInvoiceFrom(invoice: Invoice, currency = 'USD'): OwnerInvoice {
  const totals = invoiceTotals(invoice, currency);
  return {
    id: invoice.id,
    companyId: invoice.companyId,
    jobId: invoice.jobId,
    number: invoice.number,
    title: invoice.title,
    description: invoice.description,
    ...(invoice.deadline ? { deadline: invoice.deadline } : {}),
    lines: invoice.lines.map((line) => ({
      id: line.id,
      title: line.title,
      quantityThousandths: line.quantityThousandths,
      ...(line.unit ? { unit: line.unit } : {}),
      price: lineOwnerPrice(line),
      taxable: line.taxable,
    })),
    subtotal: totals.subtotal,
    tax: totals.tax,
    total: totals.total,
    paid: totals.paid,
    // Zero here, and overwritten by the trigger once it has read the job's
    // credits and deposits: this function takes one invoice and cannot
    // know about records beside it.
    credits: money(0, currency),
    deposits: money(0, currency),
    balance: totals.balance,
    ...(invoice.releasedAt ? { releasedAt: invoice.releasedAt } : {}),
    ...(invoice.viewedByOwnerAt ? { viewedByOwnerAt: invoice.viewedByOwnerAt } : {}),
    updatedAt: invoice.updatedAt,
  };
}

/** What the owner still owes across their invoices, for the portal header. */
export function ownerOutstanding(invoices: readonly OwnerInvoice[], currency = 'USD'): Money {
  return invoices.reduce(
    (running, invoice) => addMoney(running, invoice.balance),
    money(0, currency),
  );
}

/**
 * The next invoice the owner is being asked to pay, from their own copies.
 *
 * The portal's "Next invoice" card (§4). Earliest deadline first, and one
 * with no deadline comes last rather than first — "no date" is not "due
 * today", and putting it first would make the card nag about the least
 * urgent thing.
 */
export function nextOwnerInvoice(invoices: readonly OwnerInvoice[]): OwnerInvoice | undefined {
  return invoices
    .filter((invoice) => invoice.balance.amount > 0)
    .sort((a, b) => (a.deadline ?? '9999').localeCompare(b.deadline ?? '9999'))[0];
}
